// Text content + font sections (the font section is shared with date fields).
import { h } from '../dom.js';
import { defaultCharWidth } from '../../render/metrics.js';
import { section, row, full, mmInput, textInput, selectInput, toggles, message, errorLine } from './controls.js';

const BASE_TYPEFACES = ['Arial', 'Arabic'];

export function textSection(b, ctx) {
  const f = () => ctx.cur();
  return section('Text', 'text', [
    full(textInput(b, { ariaLabel: 'Printed text', multiline: true, primary: true, get: () => f()?.text, set: (v) => ctx.edit({ text: v }, 'text'), placeholder: 'Type the text to print' })),
    message(b, () => (f() && !String(f().text ?? '').length ? { kind: 'bad', text: 'Text can’t be empty — the printer rejects a blank text field.' } : null), { role: 'alert' }),
  ]);
}

const isNormalWidth = (font) => Math.abs((font.width || 0) - defaultCharWidth(font.typeface, font.height)) <= 1;

export function fontSection(b, ctx) {
  const f = () => ctx.cur();
  const font = () => f()?.font || {};
  const setFont = (patch, tag) => ctx.edit({ font: patch }, tag);

  const normalBtn = h('button', { class: 'tog', type: 'button', title: 'Reset to the typeface’s normal width for this height', style: { flex: '0 0 auto', padding: '0 10px' } }, 'Normal width');
  const normalErr = errorLine();
  normalBtn.addEventListener('click', () => {
    const fo = font();
    normalErr.report(setFont({ width: defaultCharWidth(fo.typeface, fo.height) }));
  });
  b.add(() => {
    const normal = f() ? isNormalWidth(font()) : true;
    normalBtn.disabled = normal;
    normalBtn.classList.toggle('on', normal);
    normalBtn.setAttribute('aria-pressed', String(normal));
  });

  const pf = () => f()?.font?.pointSize != null; // printer-format font: size + bold only
  const widthRow = row('Width', mmInput(b, { ariaLabel: 'Character width (mm)', get: () => font().width, set: (u) => setFont({ width: u }, 'width'), min: 10, max: 7100, step: 0.1 }), { scrub: { step: 0.05 } });
  const normalRow = full(h('div', { style: { display: 'flex', gap: '6px', alignItems: 'center' } }, normalBtn, h('span', { class: 'hint' }, 'Width stretches or squeezes the letters.')), normalErr.el);
  b.add(() => {
    widthRow.hidden = normalRow.hidden = pf();
  });

  return section('Font', 'font', [
    row(
      'Typeface',
      selectInput(b, {
        ariaLabel: 'Typeface',
        options: () => {
          const cur = font().typeface;
          const list = cur && !BASE_TYPEFACES.includes(cur) ? [...BASE_TYPEFACES, cur] : BASE_TYPEFACES;
          return list.map((t) => ({ value: t, label: t === cur && !BASE_TYPEFACES.includes(t) ? `${t} (from file)` : t }));
        },
        get: () => font().typeface,
        set: (v) => {
          const fo = font();
          return setFont(isNormalWidth(fo) ? { typeface: v, width: defaultCharWidth(v, fo.height) } : { typeface: v });
        },
      }),
    ),
    row(
      'Height',
      mmInput(b, {
        ariaLabel: 'Character height (mm)',
        get: () => font().height,
        set: (u) => {
          const fo = font();
          // keep proportions when the width was the normal one
          return setFont(isNormalWidth(fo) ? { height: u, width: defaultCharWidth(fo.typeface, u) } : { height: u }, 'height');
        },
        min: 40,
        max: 7100,
        step: 0.1,
      }),
      { scrub: { step: 0.05 } },
    ),
    widthRow,
    normalRow,
    full(
      toggles(b, [
        { label: 'B', title: 'Bold', get: () => font().bold, set: (v) => setFont({ bold: v }), style: { fontWeight: '700' } },
        { label: 'I', title: 'Italic', get: () => font().italic, set: (v) => setFont({ italic: v }), style: { fontStyle: 'italic' }, visible: () => !pf() },
        { label: 'U', title: 'Underline', get: () => font().underline, set: (v) => setFont({ underline: v }), style: { textDecoration: 'underline' }, visible: () => !pf() },
        { label: 'RTL', title: 'Right-to-left reading order (Arabic)', get: () => font().rtl, set: (v) => setFont({ rtl: v }), visible: () => !pf() },
        { label: 'Inv', title: 'Inverted (white on black) — set in the file', get: () => f()?.inverted, set: (v) => ctx.edit({ inverted: v }), visible: () => !!f()?.inverted || ctx.hadInverted },
      ]),
    ),
  ]);
}
